import type { BoardData, Item } from '../model/schema.js';
import { normalizeStandText, standOf } from '../stands/stands.js';
import { isTrunk } from '../trace/ports.js';
import { defineRule } from '../validate/rule.js';
import type { Finding, Rule } from '../validate/types.js';
import { compareFindings, suppressionReason } from '../validate/validate.js';
import { boardMaterialConfig, sparePercentFor } from './config.js';
import { MATERIAL_CATEGORIES } from '../model/schema.js';
import { isListHolder } from './list.js';

/** Values of `props.mount` / tags meaning "needs no stand". */
export const NO_STAND: ReadonlySet<string> = new Set([
  'clip',
  'handheld',
  'none',
  'boundary',
  'lavalier',
  'headset',
]);

function hasRadioOut(item: Item): boolean {
  return portsOf(item).some((p) => p.signal === 'wireless' && p.direction !== 'in');
}

function needsNoStand(data: BoardData, item: Item): boolean {
  const mount = item.props['mount'];
  if (typeof mount === 'string' && NO_STAND.has(mount.toLowerCase())) return true;
  if (item.tags.some((t) => NO_STAND.has(t.toLowerCase()))) return true;
  // An informal stand text on the item's channel already states the requirement.
  return Object.values(data.channels).some(
    (c) => c.sourceItemId === item.id && normalizeStandText(c.stand ?? '') !== '',
  );
}

function connectedPorts(data: BoardData, itemId: string): Set<string> {
  const ports = new Set<string>();
  for (const c of Object.values(data.connections)) {
    if (c.from.itemId === itemId) ports.add(c.from.portId);
    if (c.to.itemId === itemId) ports.add(c.to.portId);
  }
  return ports;
}

const items = (data: BoardData) => Object.values(data.items);
const itemLabel = (i: Item) => i.label || i.kind;
const portsOf = (i: Item) => i.ports ?? [];

/** H: a microphone is not mounted on a stand (clip / handheld wireless / tagged mics excepted). */
export const micWithoutStand = defineRule(
  'material-mic-without-stand',
  'hint',
  { en: 'Microphone without stand', de: 'Mikrofon ohne Stativ' },
  (data, emit) =>
    items(data)
      // pure list holders (U2 list boxes) are no mics (exploratory test 2026-10-11)
      .filter((i) => i.kind === 'microphone' && standOf(data, i) === undefined && !isListHolder(i))
      .filter((i) => !hasRadioOut(i) && !needsNoStand(data, i))
      .map((i) => emit([i.id], { item: itemLabel(i) })),
);

/** Mics / DIs on one stand above which sharing it is almost certainly a mistake. */
export const MAX_MICS_PER_STAND = 4;

/** H: a stand carries nothing (it is still counted as material). */
export const standUnused = defineRule(
  'material-stand-unused',
  'hint',
  { en: 'Stand without anything on it', de: 'Stativ ohne Objekt' },
  (data, emit) => {
    const used = new Set(items(data).flatMap((i) => (i.mountedOn ? [i.mountedOn] : [])));
    return items(data)
      .filter((i) => i.kind === 'stand' && !used.has(i.id))
      .map((i) => emit([i.id], { item: i.label || i.kind }));
  },
);

/** H: many mics on one stand – shared by mistake? (Theaterhaus board: 13 horn mics on one) */
export const standOverloaded = defineRule(
  'material-stand-overloaded',
  'hint',
  { en: 'Many mics on one stand', de: 'Viele Mikros an einem Stativ' },
  (data, emit) => {
    const by = new Map<string, string[]>();
    for (const i of items(data))
      if (i.mountedOn && (i.kind === 'microphone' || i.kind === 'di'))
        by.set(i.mountedOn, [...(by.get(i.mountedOn) ?? []), i.id]);
    return [...by]
      .filter(
        ([standId, ids]) => ids.length > MAX_MICS_PER_STAND && data.items[standId] !== undefined,
      )
      .map(([standId, ids]) =>
        emit([standId, ...ids], { item: itemLabel(data.items[standId]!), count: ids.length }),
      );
  },
);

/** H: an active device (power port or wattage) has no power source and no power cable. */
export const deviceWithoutPower = defineRule(
  'material-device-without-power',
  'hint',
  { en: 'Device without power', de: 'Gerät ohne Strom' },
  (data, emit) =>
    items(data)
      .filter((i) => i.kind !== 'power_distro' && i.power?.sourceId === undefined)
      .filter((i) => {
        const powerPorts = portsOf(i).filter((p) => p.signal === 'power' && p.direction !== 'out');
        if (powerPorts.length === 0 && i.power?.watts === undefined) return false;
        const connected = connectedPorts(data, i.id);
        return !powerPorts.some((p) => connected.has(p.id));
      })
      .map((i) => emit([i.id], { item: itemLabel(i) })),
);

/** H: a stage box reaches no console over network / AES / multicore links. */
export const stageboxWithoutLink = defineRule(
  'material-stagebox-without-link',
  'hint',
  { en: 'Stage box not linked to a console', de: 'Stagebox ohne Verbindung zum Pult' },
  (data, emit) => {
    const trunkLinks = Object.values(data.connections).filter((c) => {
      const a = data.items[c.from.itemId]?.ports?.find((p) => p.id === c.from.portId);
      const b = data.items[c.to.itemId]?.ports?.find((p) => p.id === c.to.portId);
      return (a !== undefined && isTrunk(a)) || (b !== undefined && isTrunk(b));
    });
    const reachesConsole = (start: string): boolean => {
      const seen = new Set([start]);
      const queue = [start];
      while (queue.length > 0) {
        const id = queue.shift()!;
        if (data.items[id]?.kind === 'console') return true;
        for (const c of trunkLinks) {
          const other =
            c.from.itemId === id ? c.to.itemId : c.to.itemId === id ? c.from.itemId : undefined;
          if (other !== undefined && !seen.has(other)) {
            seen.add(other);
            queue.push(other);
          }
        }
      }
      return false;
    };
    return items(data)
      .filter((i) => i.kind === 'stagebox' && !reachesConsole(i.id))
      .map((i) => emit([i.id], { item: itemLabel(i) }));
  },
);

/** H: a DI has nothing plugged into its input(s). */
export const diWithoutInput = defineRule(
  'material-di-without-input',
  'hint',
  { en: 'DI without instrument cable', de: 'DI ohne Instrumentenkabel' },
  (data, emit) =>
    items(data)
      .filter((i) => i.kind === 'di')
      .filter((i) => {
        const connected = connectedPorts(data, i.id);
        const inputs = portsOf(i).filter((p) => p.direction === 'in' && p.signal !== 'power');
        return !inputs.some((p) => connected.has(p.id));
      })
      .map((i) => emit([i.id], { item: itemLabel(i) })),
);

/** H: an IEM pack / transmitter is fed by no mix and no cable. */
export const iemWithoutMix = defineRule(
  'material-iem-without-mix',
  'hint',
  { en: 'IEM without mix or transmitter', de: 'IEM ohne Mix oder Sender' },
  (data, emit) => {
    const fed = new Set(Object.values(data.mixes).flatMap((m) => m.outputItemIds));
    return items(data)
      .filter((i) => i.kind === 'iem' && !fed.has(i.id))
      .filter((i) => !Object.values(data.connections).some((c) => c.to.itemId === i.id))
      .map((i) => emit([i.id], { item: itemLabel(i) }));
  },
);

/** H: a passive (speaker-level) wedge or sidefill has no amplifier connected. */
export const wedgeWithoutAmp = defineRule(
  'material-wedge-without-amp',
  'hint',
  { en: 'Passive wedge without amplifier', de: 'Passiver Monitor ohne Endstufe' },
  (data, emit) =>
    items(data)
      .filter((i) => i.kind === 'wedge' || i.kind === 'sidefill')
      .filter((i) => {
        const inputs = portsOf(i).filter((p) => p.direction === 'in' && p.signal === 'speaker');
        if (inputs.length === 0) return false;
        const connected = connectedPorts(data, i.id);
        return !inputs.some((p) => connected.has(p.id));
      })
      .map((i) => emit([i.id], { item: itemLabel(i) })),
);

/** H: the board has material but every category is configured without spares. */
export const noSpares = defineRule(
  'material-no-spares',
  'hint',
  { en: 'No spares configured', de: 'Keine Reserve eingeplant' },
  (data, emit) => {
    const config = boardMaterialConfig(data);
    const hasMaterial = items(data).some((i) => i.kind !== 'musician');
    const anySpares = MATERIAL_CATEGORIES.some((c) => sparePercentFor(config, c) > 0);
    return hasMaterial && !anySpares ? [emit([])] : [];
  },
);

/**
 * "Possibly forgotten" hints of the material list (decision U11). They are regular validation
 * rules (severity hint, i18n keys `validate.<id>.*`, suppressible per item) but are not part of
 * the default rule set, so the validation tab is not flooded; the material pane shows them.
 */
export const MATERIAL_HINT_RULES: readonly Rule[] = [
  micWithoutStand,
  standUnused,
  standOverloaded,
  deviceWithoutPower,
  stageboxWithoutLink,
  diWithoutInput,
  iemWithoutMix,
  wedgeWithoutAmp,
  noSpares,
];

/** Runs the material hint rules; suppressed findings are dropped. Sorted like `validate`. */
export function forgottenHints(data: BoardData): Finding[] {
  return MATERIAL_HINT_RULES.flatMap((r) => r.check(data))
    .filter((f) => suppressionReason(data, f) === undefined)
    .sort(compareFindings);
}
